import { ComponentType, useCallback } from 'react';
import noop from 'lodash/noop';
import { useNavigate } from 'react-router-dom';
import * as authenticationService from '../../data-layer/authentication/service';
import {
    removeCollectionAsync,
    removeSavedCollectionAsync,
    saveCollectionAsync,
    shareCollectionAsync,
    updateCollectionReactionAsync,
} from '../../data-layer/collections/actions';
import { CollectionModel, CollectionStatus } from '../../data-layer/collections/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { confirmAlert } from '../../ui/feedback/Alert';
import { contentActionsService } from '../../utils/ContentActionsService/ContentActionsService';
import { useEventSource } from '../../utils/LocationContext';
import { isBlogger } from '../../utils/accessService';
import { EventSource } from '../../utils/analyticsService';
import {
    getPageNameByEventSource,
    ymTrackCollectionGetProductFromUrlOpenDialog,
    ymTrackOpenCollectionFromProfile,
} from '../../utils/metrika';
import { useOpenDialog, useSwitchDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { useCurrentUser } from './useCurrentUser';

interface WithCollectionCallbacksProps {
    onRemove?: VoidFunction;
    onEdit?: VoidFunction;
    onShare?: VoidFunction;
    onReport?: VoidFunction;
    onReaction?: VoidFunction;
    onMove?: VoidFunction;
    onSave?: VoidFunction;
    onRemoveSaved?: VoidFunction;
}

export function withCollectionCallbacks<T>(
    BaseComponent: ComponentType<T & WithCollectionCallbacksProps>,
): ComponentType<
    Omit<Pick<T, Exclude<keyof T, keyof WithCollectionCallbacksProps>>, 'collection'> & {
        collection: CollectionModel;
    }
> {
    return (props) => {
        const { collection, ...other } = props;
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const currentUser = useCurrentUser();
        const { eventSource } = useEventSource();

        const switchDialog = useSwitchDialog();
        const openDialog = useOpenDialog();
        const onEdit = useCallback(() => {
            openDialog(DIALOGS.EDIT_COLLECTION, { collectionId: collection.id });
            contentActionsService.trackCollectionEditDialogOpen({
                id: collection.id,
                screen: eventSource ?? EventSource.COLLECTION,
            });
        }, [collection.id, eventSource, openDialog]);

        const onRemove = useCallback(async () => {
            confirmAlert('Удаление', 'Вы действительно хотите безвозвратно удалить коллекцию?')
                .then(() => dispatch(removeCollectionAsync(collection.id)))
                .then(() => {
                    if (isBlogger(currentUser?.role)) {
                        navigate(`/profile/${currentUser?.profile?.publicId}/collections`);
                    } else {
                        navigate('/profile');
                    }
                })
                .catch(noop);
        }, [dispatch, collection.id, currentUser?.role, currentUser?.profile?.publicId, navigate]);

        const onShare = useCallback(() => {
            if (
                collection.status === CollectionStatus.MODERATION ||
                collection.status === CollectionStatus.DECLINED
            ) {
                openDialog(DIALOGS.COLLECTION_STATUS, { collection: collection });
            } else {
                dispatch(
                    shareCollectionAsync(
                        collection.id,
                        collection.sharingUrl,
                        currentUser?.login,
                        currentUser?.profile?.publicId,
                        collection.isPublic,
                        collection.modifiable,
                    ),
                );
            }
        }, [collection, currentUser?.login, currentUser?.profile?.publicId, dispatch, openDialog]);

        const onReport = useCallback(() => {
            switchDialog(DIALOGS.REPORT_DIALOG, {
                targetId: collection.id,
                targetType: 'collection',
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [collection.id]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onReaction = useCallback(() => {
            dispatch(
                updateCollectionReactionAsync({
                    collectionId: collection.id,
                    login: currentUser?.login,
                    businessId: currentUser?.profile?.publicId,
                    isPublic: collection.isPublic,
                    own: collection.modifiable,
                    eventSource,
                }),
            );
        }, [
            dispatch,
            collection.id,
            collection.isPublic,
            collection.modifiable,
            currentUser?.login,
            currentUser?.profile?.publicId,
            eventSource,
        ]);

        const onMove = useCallback(() => {
            ymTrackOpenCollectionFromProfile({
                page: 'Профиль пользователя',
                userLogin: currentUser?.login,
                businessId: currentUser?.profile?.publicId,
                isPublic: collection.isPublic,
                collectionId: collection.id,
                own: true,
            });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
            collection.isPublic,
            collection.modifiable,
            collection.id,
            currentUser?.login,
            currentUser?.profile?.publicId,
        ]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onShowAddPostDialog = useCallback(
            authenticationService.protect(() => {
                if (eventSource) {
                    ymTrackCollectionGetProductFromUrlOpenDialog({
                        page: getPageNameByEventSource(eventSource),
                        collectionId: collection.id,
                    });
                }
                openDialog(DIALOGS.ADD_POST_DIALOG, { collectionId: collection.id });
            }),
            [collection.id, dispatch, eventSource, openDialog],
        );

        const moveToSavedCollections = useCallback(() => {
            if (isBlogger(currentUser?.role)) {
                navigate(
                    `/profile/${currentUser?.profile?.publicId}/collections?collectionType=saved`,
                );
            } else {
                navigate('/profile?collectionType=saved');
            }
        }, [currentUser?.profile?.publicId, currentUser?.role, navigate]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onSave = useCallback(
            authenticationService.protect(async () => {
                await dispatch(saveCollectionAsync(collection.id, moveToSavedCollections));
            }),
            [collection.id, dispatch, authenticationService.protect, saveCollectionAsync],
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
        const onRemoveSaved = useCallback(
            authenticationService.protect(() => {
                dispatch(removeSavedCollectionAsync(collection.id));
            }),
            [collection.id, dispatch, authenticationService.protect, removeSavedCollectionAsync],
        );

        return (
            // @ts-ignore
            <BaseComponent
                {...other}
                collection={collection}
                onRemove={onRemove}
                onEdit={onEdit}
                onShare={onShare}
                onReport={onReport}
                onReaction={onReaction}
                onMove={onMove}
                onShowAddPostDialog={onShowAddPostDialog}
                onSave={onSave}
                onRemoveSaved={onRemoveSaved}
            />
        );
    };
}
